import { Popover } from '@headlessui/react';
import ToastStack from 'components/desktop/shared/toast-stack';
import { IconCheck, IconUserAdd } from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import ModalWrapper from 'components/shared/modal-wrapper';
import { SITE_MAP } from 'globalConstants';
import {
  ACCEPT_FRIEND_REQUEST,
  AcceptFriendRequestData,
  AcceptFriendRequestVariables,
  CANCEL_FRIEND_REQUEST,
  CancelFriendRequestData,
  CancelFriendRequestVariables,
  DENY_FRIEND_REQUEST,
  DenyFriendRequestData,
  DenyFriendRequestVariables,
  SEND_FRIEND_REQUEST,
  STATUS,
  SendFriendRequestData,
  SendFriendRequestVariables,
  UN_FRIEND,
  UnFriendData,
  UnFriendVariables,
} from 'graphql/main/mutations';
import { useFlexMutation, useModal, useToast } from 'hooks';
import { toString } from 'lodash';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { Fragment } from 'react';
import { useFriendStatus } from '..';
import UnfriendConfirmModal from '../un-friend-confirm-modal';

const FriendButton = () => {
  const router = useRouter();
  const { asPath, push } = router;
  const { toasts, addToast, removeToast } = useToast();
  const { currentUser } = useAuthenticationContext();
  const { user, friendStatus, refetchFriendStatus } = useFriendStatus();
  const { requestId, status } = friendStatus ?? {};
  const { showing: showingUnFriendModal, toggle: toggleUnFriendModal } = useModal();

  const [mutationsSendFriendRequest] = useFlexMutation<
    SendFriendRequestData,
    SendFriendRequestVariables
  >('main', SEND_FRIEND_REQUEST);
  const [mutationCancelFriendRequest] = useFlexMutation<
    CancelFriendRequestData,
    CancelFriendRequestVariables
  >('main', CANCEL_FRIEND_REQUEST);
  const [mutationUnFriend] = useFlexMutation<UnFriendData, UnFriendVariables>('main', UN_FRIEND);
  const [mutationAcceptFriendRequest] = useFlexMutation<
    AcceptFriendRequestData,
    AcceptFriendRequestVariables
  >('main', ACCEPT_FRIEND_REQUEST);
  const [mutationDenyFriendRequest] = useFlexMutation<
    DenyFriendRequestData,
    DenyFriendRequestVariables
  >('main', DENY_FRIEND_REQUEST);

  const toggleUnFriendConfirmModal = () => {
    toggleUnFriendModal();
  };
  const sendFriendRequest = async () => {
    if (!currentUser) {
      push({
        pathname: `/${SITE_MAP.SIGN_IN.INDEX}`,
        query: queryString.stringify({ redirect: asPath }, { encode: false }),
      });
    } else {
      const { errors: sendFriendRequestError } = await mutationsSendFriendRequest({
        variables: {
          recipientId: toString(user?.id),
        },
        onCompleted: (data) => {
          data.sendFriendRequest?.success
            ? refetchFriendStatus?.()
            : addToast('error', data.sendFriendRequest?.msg ?? 'Gửi yêu cầu thất bại');
        },
        onError: (error) => {
          console.log(error);
        },
      });
      if (sendFriendRequestError)
        addToast('error', sendFriendRequestError[0]?.message ?? 'Gửi yêu cầu thất bại');
    }
  };
  const cancelFriendRequest = async () => {
    const { errors: cancelFriendRequestErrors } = await mutationCancelFriendRequest({
      variables: {
        requestId,
      },
      onCompleted: (data) => {
        data.cancelFriendRequest?.success
          ? refetchFriendStatus?.()
          : addToast('error', data.cancelFriendRequest?.msg ?? 'Huỷ yêu cầu thất bại');
      },
      onError: (error) => console.log(error),
    });
    if (cancelFriendRequestErrors)
      addToast('error', cancelFriendRequestErrors[0]?.message ?? 'Huỷ yêu cầu thất bại');
  };
  const unFriend = async () => {
    const { errors: unFriendErrors } = await mutationUnFriend({
      variables: {
        friendId: toString(user?.id),
      },
      onCompleted: (data) => {
        data.unFriend?.success
          ? refetchFriendStatus?.()
          : addToast('error', data.unFriend?.msg ?? 'Huỷ kết bạn thất bại');
      },
    });
    if (unFriendErrors) addToast('error', unFriendErrors[0]?.message ?? 'Huỷ kết bạn thất bại');
  };
  const acceptFriendRequest = async () => {
    if (requestId) {
      const { errors: acceptFriendRequestErrors } = await mutationAcceptFriendRequest({
        variables: {
          requestId: requestId,
        },
        onCompleted: (data) => {
          data.acceptFriendRequest?.success
            ? refetchFriendStatus?.()
            : addToast('error', data.acceptFriendRequest?.msg ?? 'Đồng ý kết bạn thất bại');
        },
        onError: (error) => console.log(error),
      });
      if (acceptFriendRequestErrors)
        addToast('error', acceptFriendRequestErrors[0]?.message ?? 'Đồng ý kết bạn thất bại');
    }
  };
  const denyFriendRequest = async () => {
    if (requestId) {
      const { errors: denyFriendRequestErrors } = await mutationDenyFriendRequest({
        variables: {
          requestId: requestId,
        },
        onCompleted: (data) => {
          data.denyFriendRequest?.success
            ? refetchFriendStatus?.()
            : addToast('error', data.denyFriendRequest?.msg ?? 'Từ chối kết bạn thất bại');
        },
        onError: (error) => console.log(error),
      });
      if (denyFriendRequestErrors)
        addToast('error', denyFriendRequestErrors[0]?.message ?? 'Từ chối kết bạn thất bại');
    }
  };
  return (
    <div id='friend-button' className='w-1/2'>
      {(() => {
        switch (status) {
          case STATUS.PENDING:
            return (
              <Popover className='relative flex w-full space-y-[8px]'>
                <Popover.Button
                  type='button'
                  className={`flex h-[38px] w-full items-center justify-center space-x-[6px] rounded-[6px] bg-primary transition duration-[200ms] ease-in-out hover:bg-primary-light`}
                >
                  <IconCheck className='h-[22px] w-[22px] text-paper' />
                  <span className='font-[600] text-paper'>Trả lời</span>
                </Popover.Button>
                <Popover.Panel className='absolute left-1/2 top-full z-[2] -translate-x-1/2 bg-paper'>
                  <div className='w-[131px] space-y-[16px] rounded-[8px] p-[12px] shadow-4'>
                    <div className='flex cursor-text rounded-[8px]'>
                      <ul className='flex w-full flex-col space-y-[12px]'>
                        <li>
                          <button
                            type='button'
                            className='flex w-full justify-start border-b pb-[12px]'
                            onClick={acceptFriendRequest}
                          >
                            <span className='line-clamp-1'>Xác nhận lời mời</span>
                          </button>
                        </li>
                        <li>
                          <button
                            type='button'
                            className='flex w-full justify-start'
                            onClick={denyFriendRequest}
                          >
                            <span className='line-clamp-1 text-alert'>Xóa lời mời</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Popover.Panel>
              </Popover>
            );
          case STATUS.REQUESTED:
            return (
              <button
                type='button'
                className='flex h-[38px] w-full items-center justify-center space-x-[6px] rounded-[6px] bg-primary transition duration-[200ms] ease-in-out hover:bg-primary-light'
                onClick={cancelFriendRequest}
              >
                <div className='h-[22px] w-[22px] bg-[url("/icons/remove-friend-request.svg")] bg-cover bg-center bg-no-repeat' />
                <span className='font-[600] text-paper'>Hủy lời mời</span>
              </button>
            );
          case STATUS.ACCEPTED:
            return (
              <Fragment>
                <button
                  type='button'
                  className='flex h-[38px] w-full items-center justify-center space-x-[6px] rounded-[6px] bg-tertiary font-[600] duration-[200ms] ease-in-out hover:bg-tertiary-light'
                  onClick={toggleUnFriendConfirmModal}
                >
                  <div className='h-[22px] w-[22px] bg-[url("/icons/un-friend.svg")] bg-cover bg-center bg-no-repeat' />
                  <span className='font-[600]'>Hủy kết bạn</span>
                </button>
                <ModalWrapper
                  isClickAway
                  showing={showingUnFriendModal}
                  position='center'
                  onToggle={toggleUnFriendConfirmModal}
                >
                  <UnfriendConfirmModal
                    onCancel={toggleUnFriendConfirmModal}
                    onConfirm={unFriend}
                  />
                </ModalWrapper>
              </Fragment>
            );
          default:
            return (
              <button
                type='button'
                className='flex h-[38px] w-full items-center justify-center space-x-[6px] rounded-[6px] bg-primary transition duration-[200ms] ease-in-out hover:bg-primary-light'
                onClick={sendFriendRequest}
              >
                <IconUserAdd className='h-[22px] w-[22px] text-paper' />
                <span className='font-[600] text-paper'>Thêm bạn</span>
              </button>
            );
        }
      })()}
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default FriendButton;
